import styled from 'styled-components';

import { PreferencesFormMeta, PreferencesFormValues } from 'components/notificationPreferences/NotificationPreferences';
import { MultiSelectChips } from 'components/shared/form/InfiniteScrollDropdown.tsx';
import InputGroup from 'components/shared/form/InputGroup';
import Toggle from 'components/shared/Toggle.tsx';
import { TabType } from 'components/Tabs';
import { PHYSICIAN_TEAM } from 'constants/locationTypes';
import { useForm } from 'context/form';
import AttrValue from 'models/AttrValue';
import Classification, { ClassificationApiName } from 'models/Classification.ts';
import Group from 'models/Group.ts';
import { Notification } from 'models/Notifications';
import User from 'models/User';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { useInfiniteClassifications } from 'services/api/classification';
import { useInfiniteGroups } from 'services/api/group';
import { useInfiniteUsers } from 'services/api/user';
import { BodySmall, H3 } from 'styles/typography';

import NotificationCategoryPreferences from './NotificationCategoryPreferences';

function NotificationPreferencesContainer({
  clientId,
  caseManagerEnabled,
  utilizationManagerEnabled,
  selectedGroupType,
  canActAsManager,
  disabled: disabledProp = false,
  showInboundReferrals,
}: {
  clientId: string;
  caseManagerEnabled: boolean;
  utilizationManagerEnabled: boolean;
  selectedGroupType: TabType;
  canActAsManager: boolean;
  disabled?: boolean;
  showInboundReferrals: boolean;
}) {
  const { values, setValue } = useForm<PreferencesFormValues, PreferencesFormMeta>();

  const groupTypeValues = (values[selectedGroupType.value] ?? {}) as Notification;

  const name = selectedGroupType.label;

  // Every picker is keyed to the selected client explicitly. Groups use the
  // owned-or-network filter so facilities the client coordinates through a
  // network stay scopeable; classifications use forClient (catalog semantics:
  // linked rows plus the global unarchived health plans).
  const asyncGroupOptions = useInfiniteGroups({
    type: groupTypeValues.groupTypeApiName,
    include: 'groupType',
    sortBy: 'name asc',
    clientIdOrNetworkClientId: clientId,
    enforceScopedClient: false,
  });

  const asyncPlanTypeOptions = useInfiniteClassifications({
    type: ClassificationApiName.PLAN_TYPE,
    sortBy: 'name',
    forClient: clientId,
  });

  const asyncEpisodeTypeOptions = useInfiniteClassifications({
    type: ClassificationApiName.EPISODE,
    sortBy: 'name',
    forClient: clientId,
  });

  // Membership under the selected client — the same population the legacy
  // acting-client scope produced. (The old `client` param was never permitted
  // on this endpoint, so home-client filtering was always inert.)
  const asyncUserOptions = useInfiniteUsers({
    include: 'credential',
    sortBy: 'name',
    active: true,
    memberOfClient: clientId,
  });

  const asyncPhysicianTeamOptions = useInfiniteAttrValues({
    type: PHYSICIAN_TEAM,
    distinctDisplayName: true,
    client: clientId,
  });

  return (
    <ToggleAndFormContainer>
      <Banner $active={!!groupTypeValues.active}>
        <BannerText>
          <BannerTitle>Care Coordination Notifications - {name}</BannerTitle>
          <BannerSubtitle>
            All notifications are {groupTypeValues.active ? 'on' : 'off'} for Care Coordination - {name}
          </BannerSubtitle>
        </BannerText>
        <Toggle
          active={!!groupTypeValues.active}
          disabled={disabledProp}
          onChange={(active) => setValue(`${selectedGroupType.value}.active`, active)}
        />
      </Banner>
      <StyledFormContainer>
        <SectionContainer>
          <StyledH3>Notification Criteria</StyledH3>
          <SectionDescription>
            Indicate below if you wish to only receive notifications for {name} patients that meet a specific set of
            criteria.
          </SectionDescription>
          <InputGroup>
            <MultiSelectChips<Group>
              title={`Specific ${name}s`}
              {...asyncGroupOptions}
              disabled={disabledProp || !groupTypeValues.active}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              onChange={(groups) => {
                setValue(`${selectedGroupType.value}.scopes.groups`, groups);
              }}
              values={groupTypeValues.scopes?.groups ?? []}
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips<Classification>
              title='Specific Plan Types'
              {...asyncPlanTypeOptions}
              disabled={disabledProp || !groupTypeValues.active}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              onChange={(planTypes) => {
                setValue(`${selectedGroupType.value}.scopes.planTypes`, planTypes);
              }}
              values={groupTypeValues.scopes?.planTypes ?? []}
            />
          </InputGroup>

          <InputGroup>
            <MultiSelectChips<Classification>
              title='Specific Episode Types'
              {...asyncEpisodeTypeOptions}
              disabled={disabledProp || !groupTypeValues.active}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              onChange={(episodes) => {
                setValue(`${selectedGroupType.value}.scopes.episodes`, episodes);
              }}
              values={groupTypeValues.scopes?.episodes ?? []}
            />
          </InputGroup>

          {utilizationManagerEnabled && (
            <InputGroup>
              <MultiSelectChips<User>
                title='Specific Utilization Managers'
                {...asyncUserOptions}
                disabled={disabledProp || !groupTypeValues.active}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                onChange={(users) => {
                  setValue(`${selectedGroupType.value}.scopes.utilizationManagers`, users);
                }}
                values={groupTypeValues.scopes?.utilizationManagers ?? []}
              />
            </InputGroup>
          )}

          {caseManagerEnabled && (
            <InputGroup>
              <MultiSelectChips<User>
                title='Specific Case Managers'
                {...asyncUserOptions}
                disabled={disabledProp || !groupTypeValues.active}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                onChange={(users) => {
                  setValue(`${selectedGroupType.value}.scopes.caseManagers`, users);
                }}
                values={groupTypeValues.scopes?.caseManagers ?? []}
              />
            </InputGroup>
          )}

          {canActAsManager && (
            <InputGroup>
              <MultiSelectChips<AttrValue>
                title='Specific Physicians'
                {...asyncPhysicianTeamOptions}
                disabled={disabledProp || !groupTypeValues.active}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                onChange={(physicianTeams) => {
                  setValue(`${selectedGroupType.value}.scopes.physicianTeams`, physicianTeams);
                }}
                values={groupTypeValues.scopes?.physicianTeams ?? []}
              />
            </InputGroup>
          )}
        </SectionContainer>
        <SelectSection>
          <StyledH3>Select Notifications</StyledH3>
          <SectionDescription>Choose which Care Coordination events trigger a notification</SectionDescription>
          {['criticalAlerts', 'activity', 'patientStatusChanges', 'referrals'].map((category) => (
            <NotificationCategoryPreferences
              key={category}
              category={category}
              selectedTab={selectedGroupType}
              disabled={disabledProp || !groupTypeValues.active}
              showInboundReferrals={showInboundReferrals}
            />
          ))}
        </SelectSection>
      </StyledFormContainer>
    </ToggleAndFormContainer>
  );
}

const Banner = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.blue100 : theme.colors.black10)};
  background-color: ${({ $active, theme }) => ($active ? theme.colors.blue5 : theme.colors.white)};
  padding: 16px 24px;
  transition: 0.2s;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BannerTitle = styled.div`
  color: ${({ theme }) => theme.colors.black100};
  font-size: 14px;
  font-weight: 500;
`;

const BannerSubtitle = styled(BodySmall)`
  font-size: 12px;
`;

const SectionContainer = styled.div`
  height: fit-content;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.black10};
  background-color: ${(props) => props.theme.colors.white};
  padding: 24px;
  width: 100%;
`;

const SelectSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledH3 = styled(H3)`
  margin-bottom: 12px;
`;

const StyledFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

const ToggleAndFormContainer = styled.div`
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionDescription = styled(BodySmall)`
  font-size: 12px;
  margin-bottom: 24px;
`;

export default NotificationPreferencesContainer;
